import React, { useCallback, useEffect, useState } from "react";
import { getDedupLog, getDedupStats, overrideDedupEntry, ApiError } from "../api.js";

// ---------------------------------------------------------------------------
// Status badge colours (monochrome — brightness-based, matches app theme)
// ---------------------------------------------------------------------------
const STATUS_LABELS = {
  accepted: "Accepted",
  duplicate_file: "Duplicate File",
  duplicate_io: "Duplicate IO",
  overridden: "Overridden",
};

function StatusBadge({ status }) {
  return <span className={`dedup-badge dedup-badge-${status}`}>{STATUS_LABELS[status] ?? status}</span>;
}

// ---------------------------------------------------------------------------
// Source icon
// ---------------------------------------------------------------------------
const SOURCE_ICONS = {
  upload: "📄",
  email: "📧",
  slack: "💬",
  api: "🔌",
};

// ---------------------------------------------------------------------------
// Format timestamp to short local time
// ---------------------------------------------------------------------------
function fmtTime(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return ts;
  }
}

// ---------------------------------------------------------------------------
// Truncate hash for display
// ---------------------------------------------------------------------------
function shortHash(hash) {
  if (!hash) return "—";
  return hash.length > 12 ? `${hash.slice(0, 8)}…` : hash;
}

// ---------------------------------------------------------------------------
// DedupLog — the main dedup audit log page
// ---------------------------------------------------------------------------
export default function DedupLog() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(25);

  // Filters
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSource, setFilterSource] = useState("");

  // Override dialog
  const [overrideEntry, setOverrideEntry] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = { page, limit };
      if (filterStatus) query.status = filterStatus;
      if (filterSource) query.source = filterSource;

      const [logResult, statsResult] = await Promise.all([
        getDedupLog(query),
        getDedupStats().catch(() => null),
      ]);

      setEntries(logResult?.data ?? []);
      setTotal(logResult?.total ?? 0);
      if (statsResult) setStats(statsResult);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [page, limit, filterStatus, filterSource]);

  useEffect(() => {
    load();
  }, [load]);

  function flash(msg, kind = "ok") {
    setActionMsg({ msg, kind });
    setTimeout(() => setActionMsg(null), 4000);
  }

  async function handleOverride() {
    if (!overrideEntry || !overrideReason.trim()) return;
    setOverrideSubmitting(true);
    try {
      await overrideDedupEntry(overrideEntry.id, overrideReason.trim());
      flash(`Override recorded for ${overrideEntry.ioNumber ?? overrideEntry.id}.`);
      setOverrideEntry(null);
      setOverrideReason("");
      await load();
    } catch (err) {
      flash(`Override failed: ${err instanceof ApiError ? err.message : String(err)}`, "error");
    } finally {
      setOverrideSubmitting(false);
    }
  }

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="dedup-log container">
      <div className="dedup-header">
        <h2>Deduplication Audit Log</h2>
        <p className="dedup-subtitle">
          Every ingestion attempt is logged here — accepted, duplicate, or overridden.
        </p>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="dedup-stats-row">
          <div className="dedup-stat-card">
            <span className="dedup-stat-value">{stats.total ?? 0}</span>
            <span className="dedup-stat-label">Total</span>
          </div>
          <div className="dedup-stat-card">
            <span className="dedup-stat-value">{stats.accepted ?? 0}</span>
            <span className="dedup-stat-label">Accepted</span>
          </div>
          <div className="dedup-stat-card">
            <span className="dedup-stat-value">{stats.duplicateFile ?? 0}</span>
            <span className="dedup-stat-label">Dup. File</span>
          </div>
          <div className="dedup-stat-card">
            <span className="dedup-stat-value">{stats.duplicateIo ?? 0}</span>
            <span className="dedup-stat-label">Dup. IO</span>
          </div>
          <div className="dedup-stat-card">
            <span className="dedup-stat-value">{stats.overridden ?? 0}</span>
            <span className="dedup-stat-label">Overridden</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="dedup-filters">
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
          className="dedup-filter-select"
        >
          <option value="">All statuses</option>
          <option value="accepted">Accepted</option>
          <option value="duplicate_file">Duplicate File</option>
          <option value="duplicate_io">Duplicate IO</option>
          <option value="overridden">Overridden</option>
        </select>
        <select
          value={filterSource}
          onChange={(e) => { setFilterSource(e.target.value); setPage(1); }}
          className="dedup-filter-select"
        >
          <option value="">All sources</option>
          <option value="upload">Upload</option>
          <option value="email">Email</option>
          <option value="slack">Slack</option>
          <option value="api">API</option>
        </select>
        <button className="btn dedup-filter-refresh" onClick={load}>
          ↻ Refresh
        </button>
      </div>

      {/* Action message */}
      {actionMsg && (
        <div className={`alert ${actionMsg.kind === "error" ? "alert-error" : "alert-ok"}`}>
          {actionMsg.msg}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="alert alert-error">Failed to load dedup log: {error}</div>
      )}

      {/* Table */}
      {loading ? (
        <p className="dedup-loading">Loading…</p>
      ) : entries.length === 0 ? (
        <div className="dedup-empty">
          <p>No ingestion attempts logged yet.</p>
          <p className="dedup-empty-hint">
            Upload an IO file or create one via the API to see dedup entries here.
          </p>
        </div>
      ) : (
        <>
          <table className="dedup-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Status</th>
                <th>Source</th>
                <th>IO Number</th>
                <th>Filename</th>
                <th>File Hash</th>
                <th>Fingerprint</th>
                <th>Duplicate Of</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className={`dedup-row dedup-row-${entry.status}`}>
                  <td className="mono">{fmtTime(entry.timestamp)}</td>
                  <td><StatusBadge status={entry.status} /></td>
                  <td>
                    <span title={entry.source}>
                      {SOURCE_ICONS[entry.source] ?? "❓"} {entry.source}
                    </span>
                  </td>
                  <td className="mono">{entry.ioNumber ?? "—"}</td>
                  <td>{entry.filename ?? "—"}</td>
                  <td className="mono">{shortHash(entry.fileHash)}</td>
                  <td className="mono">{shortHash(entry.fingerprint)}</td>
                  <td className="mono">{entry.duplicateOf ? shortHash(entry.duplicateOf) : "—"}</td>
                  <td>
                    {(entry.status === "duplicate_file" || entry.status === "duplicate_io") && (
                      <button
                        className="btn btn-sm dedup-override-btn"
                        onClick={() => { setOverrideEntry(entry); setOverrideReason(""); }}
                      >
                        Override
                      </button>
                    )}
                    {entry.status === "overridden" && entry.overrideReason && (
                      <span className="dedup-override-reason" title={entry.overrideReason}>
                        ✓ {entry.overrideReason.slice(0, 30)}
                        {entry.overrideReason.length > 30 ? "…" : ""}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="dedup-pagination">
            <button
              className="btn btn-sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Prev
            </button>
            <span className="dedup-page-info">
              Page {page} of {totalPages || 1} ({total} entries)
            </span>
            <button
              className="btn btn-sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </button>
          </div>
        </>
      )}

      {/* Override dialog */}
      {overrideEntry && (
        <div className="dedup-overlay" onClick={() => setOverrideEntry(null)}>
          <div className="dedup-override-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Override Duplicate Flag</h3>
            <p>
              You are about to manually accept an ingestion that was flagged as
              <strong> {STATUS_LABELS[overrideEntry.status] ?? overrideEntry.status}</strong>.
            </p>
            <div className="dedup-override-detail">
              <span>IO Number:</span> <code>{overrideEntry.ioNumber ?? "—"}</code><br />
              <span>Filename:</span> <code>{overrideEntry.filename ?? "—"}</code><br />
              <span>Source:</span> <code>{overrideEntry.source}</code>
            </div>
            <label className="dedup-override-label">
              Reason (required):
              <textarea
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="e.g. Corrected re-upload after parsing error"
                rows={3}
                autoFocus
              />
            </label>
            <div className="dedup-override-actions">
              <button className="btn" onClick={() => setOverrideEntry(null)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleOverride}
                disabled={!overrideReason.trim() || overrideSubmitting}
              >
                {overrideSubmitting ? "Submitting…" : "Confirm Override"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}